import type { ColDef, EditableCallbackParams, ValueGetterParams, ValueSetterParams } from 'ag-grid-community';
import { checkIsNumber, restrictToTwoDecimals } from '../../../../Components/helper';
import type {
  ArrKey,
  BoolKey,
  CheckedList,
  FillFieldWriter,
  FillOptionsResolver,
  FillRowGate,
  FillSpec,
  FillTextNormalizer,
  FillValue,
  LineValue,
  MultiSubmitGridContext,
  MultiSubmitRow,
  OptionSource,
  OptionsKey,
  SelectOption,
  StrKey,
} from './types';
import { isCalendarDay, splitListLabels } from '../../../../Components/BombiixAGGrid/fillClipboard';

const LIST_SEPARATOR = ', ';
export const LINE_SEPARATOR = '; ';
const EMPTY_CHECKED_LIST: CheckedList = { checked: false, values: [] };

/**
 * Default row/line gate.
 * @returns true.
 */
export function alwaysEnabled(): boolean {
  return true;
}

/**
 * Value comparison for fillable columns: never equal, so AG Grid repaints the cell on every row update.
 * The cells also render from other row fields (the costing supplier gates costing cells, the grading
 * group scopes sizes, the sample group enables sample type), and AG Grid force-refreshed these
 * value-less columns before they had a getter — comparing real values would leave those cells stale.
 * @returns false.
 */
export function alwaysRepaint(): boolean {
  return false;
}

/**
 * Default text normaliser.
 * @param text - the text.
 * @returns the text unchanged.
 */
export function keepText(text: string): string {
  return text;
}

/**
 * Narrows a fill value to text.
 * @param value - the value.
 * @returns the text, or '' for any other shape.
 */
export function asText(value: FillValue | LineValue): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Narrows a value to a list of ids.
 * @param value - the value.
 * @returns the string items, or [] for any other shape.
 */
export function asList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

/**
 * Narrows a fill value to stacked lines.
 * @param value - the value.
 * @returns the lines, or [] for any other shape.
 */
export function asLines(value: FillValue): LineValue[] {
  return Array.isArray(value) ? value : [];
}

/**
 * Narrows a fill value to a checkbox + selection.
 * @param value - the value.
 * @returns the value, or an unticked empty selection for any other shape.
 */
function asCheckedList(value: FillValue): CheckedList {
  return typeof value === 'object' && !Array.isArray(value) ? { checked: !!value.checked, values: asList(value.values) } : EMPTY_CHECKED_LIST;
}

/**
 * The label of an option id; '' when unknown, so an id never reaches the clipboard.
 * @param options - the options.
 * @param value - the id.
 * @returns the label.
 */
export function labelOf(options: SelectOption[], value: string): string {
  return options.find((option) => option.value === value)?.label ?? '';
}

/**
 * The option id matching pasted text, by label (trimmed, case-insensitive) or by id.
 * @param options - the options.
 * @param text - the pasted text.
 * @returns the id, or '' when nothing matches.
 */
export function valueOf(options: SelectOption[], text: string): string {
  const needle = text.trim().toLowerCase();
  return options.find((option) => String(option.label ?? '').trim().toLowerCase() === needle || option.value === text.trim())?.value ?? '';
}

/**
 * Labels of several ids, joined for the clipboard.
 * @param options - the options.
 * @param values - the ids.
 * @returns the joined labels.
 */
export function labelsOf(options: SelectOption[], values: string[]): string {
  return values
    .map((value) => labelOf(options, value))
    .filter(Boolean)
    .join(LIST_SEPARATOR);
}

/**
 * Ids for a pasted list of labels; labels with no match are dropped. A label that itself contains a
 * comma ("ABC Textiles, Ltd") still matches as one label.
 * @param options - the options.
 * @param text - the pasted text.
 * @returns the matched ids.
 */
export function valuesOf(options: SelectOption[], text: string): string[] {
  return splitListLabels(text, LIST_SEPARATOR.trim(), (label) => valueOf(options, label) || undefined).filter((id): id is string => Boolean(id));
}

/**
 * Options for a select cell, resolved in the renderers' priority order: inline → per-row → page-level.
 * @param source - the column's option source.
 * @param row - the row whose per-product options apply.
 * @param ctx - grid context.
 * @returns the options, or [] when none are configured.
 */
export function resolveOptions(source: OptionSource, row: MultiSubmitRow, ctx: MultiSubmitGridContext): SelectOption[] {
  if (source.options) return source.options;
  const perRow = source.optionsKey ? (ctx.optionsFor(row.id)[source.optionsKey] as SelectOption[] | undefined) : undefined;
  if (perRow) return perRow;
  return (source.ctxOptionsKey ? ctx[source.ctxOptionsKey] : undefined) ?? [];
}

/**
 * Builds an options resolver from a column's option source.
 * @param source - the option source.
 * @returns the resolver.
 */
export function optionsFrom(source: OptionSource): FillOptionsResolver {
  return (row, ctx) => resolveOptions(source, row, ctx);
}

/**
 * Sample Type options for a row: the selected group's automation types, or the "other" master list.
 * @param row - the row.
 * @param ctx - grid context, or just the two option sources it reads.
 * @returns the options.
 */
export function sampleTypeOptions(row: MultiSubmitRow, ctx: Pick<MultiSubmitGridContext, 'optionsFor' | 'otherSamplesList'>): SelectOption[] {
  if (row.typeOfSampleType === 'other') return ctx.otherSamplesList.map((sample) => ({ value: sample._id, label: sample.name }));
  const group = ctx.optionsFor(row.id).sampleTypeGroupRaw.find((sampleGroup) => sampleGroup._id === row.sampleTypeGroup);
  return (group?.sampleTypeAutomation ?? [])
    .map((automation) => automation.sampleType)
    .filter((sampleType): sampleType is { _id: string; name: string } => !!sampleType?._id)
    .map((sampleType) => ({ value: sampleType._id, label: sampleType.name }));
}

/**
 * Two-decimal numeric text, or '' when the text is not a plain number — the rule the costing inputs apply.
 * @param text - raw text.
 * @returns the restricted number text, or ''.
 */
export function toNumericText(text: string): string {
  const restricted = restrictToTwoDecimals(text.trim());
  return restricted && checkIsNumber(restricted) ? restricted : '';
}

/**
 * Normalises a pasted due date; only a real day in the ISO `yyyy-mm-dd` form the date cell stores is
 * accepted, so 2024-13-45 or 2024-02-30 is refused.
 * @param text - raw text.
 * @returns the date, or ''.
 */
export function toIsoDate(text: string): string {
  return isCalendarDay(text.trim()) ? text.trim() : '';
}

/**
 * True when the row has a Costing Information supplier — the gate on every costing control.
 * @param row - the row.
 * @returns whether costing cells are editable.
 */
export function hasCostingSupplier(row: MultiSubmitRow): boolean {
  return !!row.ciSupplier;
}

/**
 * Whether a cell may take a fill or paste: a top supplier is chosen and the column's own gate passes.
 * @param spec - the column's spec.
 * @param row - the target row.
 * @param ctx - grid context.
 * @returns true when writable.
 */
export function isFillable(spec: FillSpec, row: MultiSubmitRow | undefined, ctx: MultiSubmitGridContext): boolean {
  return !!row && !!ctx.selectedSupplierId && spec.isEnabled(row);
}

/**
 * A label checkbox. Copying only ever ticks a target, never unticks it (Push's rule).
 * @param field - the boolean field.
 * @returns the spec.
 */
export function flagSpec(field: BoolKey): FillSpec {
  return {
    read: (row) => row[field],
    apply: (value, row, ctx) => {
      if (value === true && !row[field]) ctx.updateRow(row.id, { [field]: true } as Partial<MultiSubmitRow>);
    },
    toText: (value, _row, ctx) => (value === true ? String(ctx.t('PILL.YES')) : ''),
    fromText: (text, _row, ctx) => text.trim().toLowerCase() === String(ctx.t('PILL.YES')).toLowerCase(),
    isEnabled: alwaysEnabled,
  };
}

/**
 * A checkbox + multi-select. Values outside the target's options are dropped; with none left the
 * selection is kept and only a ticked checkbox carries over.
 * @param checkedField - the checkbox field.
 * @param valueField - the selected-options field.
 * @param optionsKey - the per-product options the values must belong to.
 * @returns the spec.
 */
export function checkedListSpec(checkedField: BoolKey, valueField: ArrKey, optionsKey: OptionsKey): FillSpec {
  const optionsOf = optionsFrom({ optionsKey });
  return {
    read: (row) => ({ checked: row[checkedField], values: row[valueField].map((option) => option.value) }),
    apply: (value, row, ctx) => {
      const { checked, values } = asCheckedList(value);
      const picked = optionsOf(row, ctx).filter((option) => values.includes(option.value));
      if (picked.length > 0) ctx.updateRow(row.id, { [valueField]: picked, [checkedField]: checked } as Partial<MultiSubmitRow>);
      else if (checked && !row[checkedField]) ctx.updateRow(row.id, { [checkedField]: true } as Partial<MultiSubmitRow>);
    },
    toText: (value, row, ctx) => labelsOf(optionsOf(row, ctx), asCheckedList(value).values),
    fromText: (text, row, ctx) => {
      const values = valuesOf(optionsOf(row, ctx), text);
      return { checked: values.length > 0, values };
    },
    isEnabled: alwaysEnabled,
  };
}

/**
 * A single select. A blank or out-of-options value leaves the target untouched.
 * @param field - the stored id field.
 * @param optionsOf - the target row's options.
 * @param write - how to store the value; defaults to a plain row patch.
 * @param isEnabled - row gate.
 * @returns the spec.
 */
export function selectSpec(field: StrKey, optionsOf: FillOptionsResolver, write?: FillFieldWriter, isEnabled: FillRowGate = alwaysEnabled): FillSpec {
  const writeValue: FillFieldWriter = write ?? ((ctx, rowId, value) => ctx.updateRow(rowId, { [field]: value } as Partial<MultiSubmitRow>));
  return {
    read: (row) => row[field],
    apply: (value, row, ctx) => {
      const id = asText(value);
      if (!id || id === row[field] || !optionsOf(row, ctx).some((option) => option.value === id)) return;
      writeValue(ctx, row.id, id);
    },
    toText: (value, row, ctx) => labelOf(optionsOf(row, ctx), asText(value)),
    fromText: (text, row, ctx) => valueOf(optionsOf(row, ctx), text),
    isEnabled,
  };
}

/**
 * A free-text cell. Blank (or, after `normalize`, invalid) text leaves the target untouched.
 * @param field - the text field.
 * @param normalize - cleans the text; returns '' to reject it.
 * @param isEnabled - row gate.
 * @returns the spec.
 */
export function textSpec(field: StrKey, normalize: FillTextNormalizer = keepText, isEnabled: FillRowGate = alwaysEnabled): FillSpec {
  return {
    read: (row) => row[field],
    apply: (value, row, ctx) => {
      const text = normalize(asText(value));
      if (text && text !== row[field]) ctx.updateRow(row.id, { [field]: text } as Partial<MultiSubmitRow>);
    },
    toText: (value) => asText(value),
    fromText: (text) => text,
    isEnabled,
  };
}

/**
 * Attaches fill + clipboard support to a column. Writes go through the context's queue rather than
 * mutating `data`, so React state stays the only source of truth.
 * @param colDef - the column.
 * @param spec - how the column fills.
 * @returns the column with getter, setter, editable gate and the always-repaint comparison.
 */
export function withFill(colDef: ColDef<MultiSubmitRow>, spec: FillSpec): ColDef<MultiSubmitRow> {
  return {
    ...colDef,
    context: { fill: spec },
    valueGetter: ({ data }: ValueGetterParams<MultiSubmitRow>) => (data ? spec.read(data) : undefined),
    valueSetter: ({ data, newValue, context }: ValueSetterParams<MultiSubmitRow>) => {
      const ctx = context as MultiSubmitGridContext;
      if (data && newValue !== null && newValue !== undefined) ctx.queueCellWrite(data.id, () => spec.apply(newValue, data, ctx));
      return false;
    },
    editable: ({ data, context }: EditableCallbackParams<MultiSubmitRow>) => isFillable(spec, data, context as MultiSubmitGridContext),
    equals: alwaysRepaint,
  };
}

/**
 * The fill spec a column was built with, if any.
 * @param colDef - the column.
 * @returns the spec, or undefined for columns that do not fill.
 */
export function fillSpecOf(colDef: ColDef<MultiSubmitRow>): FillSpec | undefined {
  const { context }: { context?: unknown } = colDef;
  return typeof context === 'object' && context !== null && 'fill' in context ? (context as { fill: FillSpec }).fill : undefined;
}

/**
 * The Sample Type select. The value is resolved when the write lands, against the row's Sample Type
 * Group as it is by then, so a group pasted in the same row is the one the type is checked against;
 * a pasted label the current group does not offer is passed on as text for that later check.
 * @returns the spec.
 */
export function sampleTypeSpec(): FillSpec {
  return {
    read: (row) => row.sampleType,
    apply: (value, row, ctx) => {
      const idOrLabel = asText(value);
      if (idOrLabel) ctx.fillSampleType(row.id, idOrLabel);
    },
    toText: (value, row, ctx) => labelOf(sampleTypeOptions(row, ctx), asText(value)),
    fromText: (text, row, ctx) => valueOf(sampleTypeOptions(row, ctx), text) || text.trim(),
    isEnabled: (row) => !!row.sampleTypeGroup,
  };
}
